/**
 * Unified pointer input: collapses mouse + touch + pen into "down" / "up" events.
 * Single-pointer model — only the first active pointer is tracked.
 */

export type PointerHandler = (worldX: number, worldY: number) => void;

export interface InputCallbacks {
  onDown: PointerHandler;
  onUp: PointerHandler;
  /** Optional move handler; only fires while the active pointer is down. */
  onMove?: PointerHandler;
}

export class Input {
  private activePointerId: number | null = null;

  constructor(
    private readonly target: HTMLElement,
    private readonly cb: InputCallbacks,
  ) {
    target.addEventListener('pointerdown', this.handleDown, { passive: false });
    target.addEventListener('pointerup', this.handleUp, { passive: false });
    target.addEventListener('pointercancel', this.handleUp, { passive: false });
    target.addEventListener('pointerleave', this.handleUp, { passive: false });
    target.addEventListener('pointermove', this.handleMove, { passive: false });
  }

  private handleDown = (e: PointerEvent): void => {
    if (this.activePointerId !== null) return;
    e.preventDefault();
    this.activePointerId = e.pointerId;
    this.cb.onDown(e.clientX, e.clientY);
  };

  private handleUp = (e: PointerEvent): void => {
    if (this.activePointerId !== e.pointerId) return;
    e.preventDefault();
    this.activePointerId = null;
    this.cb.onUp(e.clientX, e.clientY);
  };

  private handleMove = (e: PointerEvent): void => {
    if (this.activePointerId !== e.pointerId) return;
    if (!this.cb.onMove) return;
    this.cb.onMove(e.clientX, e.clientY);
  };

  destroy(): void {
    this.target.removeEventListener('pointerdown', this.handleDown);
    this.target.removeEventListener('pointerup', this.handleUp);
    this.target.removeEventListener('pointercancel', this.handleUp);
    this.target.removeEventListener('pointerleave', this.handleUp);
    this.target.removeEventListener('pointermove', this.handleMove);
  }
}
